import apiUrl from './api';
import { getWithAuth, postWithAuth } from '@/lib/httpAdapter';

export interface FeatureFlagsResponse {
  OrgUUID: string;
  Features: Record<string, boolean>;
  Error?: string;
}

/**
 * Feature-flag key for the org-wide "hide the leaderboard from our own members"
 * setting (ENT-3531). Stored with "hidden" polarity: enabled => leaderboard
 * hidden org-wide; absent/disabled => visible (default). Kept distinct from the
 * global-leaderboard opt-out in `leaderboardVisibility.ts`.
 */
export const LEADERBOARD_HIDDEN_FLAG = 'leaderboard_hidden';

export const getFeatureFlags = async (
  orgUUID: string
): Promise<FeatureFlagsResponse> => {
  const endpoint = `${apiUrl}featureFlags/?OrgUUID=${encodeURIComponent(orgUUID)}`;
  const response = await getWithAuth(endpoint);
  if (!response.ok) {
    throw new Error(`Failed to fetch feature flags: ${response.status}`);
  }
  const raw = await response.json();
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
};

export interface SetFeatureFlagResult {
  Error: string | null;
}

/**
 * Enable or disable an org feature flag (admin-only on the backend).
 *
 * @param orgUUID - Organization to update.
 * @param featureName - Flag key (e.g. `LEADERBOARD_HIDDEN_FLAG`).
 * @param enabled - Desired enabled state.
 * @returns `{ Error: null }` on success, or a non-null humane error string.
 */
export const setFeatureFlag = async (
  orgUUID: string,
  featureName: string,
  enabled: boolean
): Promise<SetFeatureFlagResult> => {
  try {
    const res = await postWithAuth(`${apiUrl}featureFlags/enable/`, {
      OrgUUID: orgUUID,
      FeatureName: featureName,
      Enabled: enabled,
    });
    if (!res.ok) {
      return { Error: `HTTP ${res.status}` };
    }
    return { Error: null };
  } catch (e) {
    return { Error: e instanceof Error ? e.message : 'Network error' };
  }
};

/**
 * Convenience wrapper: hide or show the org-wide leaderboard (ENT-3531).
 *
 * @param orgUUID - Organization to update.
 * @param hidden - `true` hides the leaderboard for every member; `false` restores it.
 */
export const setLeaderboardHidden = async (
  orgUUID: string,
  hidden: boolean
): Promise<SetFeatureFlagResult> =>
  setFeatureFlag(orgUUID, LEADERBOARD_HIDDEN_FLAG, hidden);
